const { trapFocus, disableTrapFocus } = window.BAO.Utils

export class WishlistApp {
  constructor () {
    this.isDrawerOpen = false
    this.activeTrigger = null
    this.closeIconsWithListener = new WeakSet()

    this.handleClick = this.handleClick.bind(this)
    document.addEventListener('click', this.handleClick)

    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)

    this.updateWishlistCount()

    this.observeDrawer()

    this.setupListeners()
  }

  setupListeners () {
    document.addEventListener(
      window.BAO.EVENTS.VARIANT.CHANGED,
      this.onVariantChanged.bind(this),
    )
  }

  observeDrawer () {
    this.updatePriceFormatting = this.updatePriceFormatting.bind(this)

    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        for (let i = 0; i < mutation.addedNodes.length; i++) {
          if (
            mutation.addedNodes[i].classList &&
            (mutation.addedNodes[i].classList.contains('productContainer') ||
              mutation.addedNodes[i].classList.contains('iwish-row'))
          ) {
            this.updatePriceFormatting()
          }
        }

        if (mutation.type === 'childList' && this.isDrawerOpen) {
          const liveContainer = document.querySelector(this.drawerMenuSelector)
          if (liveContainer) this.makeCloseIconAccessible(liveContainer)
        }

        if (
          mutation.type === 'attributes' &&
          mutation.target.classList?.contains(this.drawerVisibilityClass)
        ) {
          this.handleDrawerVisibilityChange(
            mutation.target.classList.contains('show'),
          )
        }
      })
    })

    const drawerEl = document.querySelector(this.drawerSelector)

    if (drawerEl) {
      observer.observe(drawerEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      })
    }
  }

  handleDrawerVisibilityChange (isOpen) {
    if (isOpen === this.isDrawerOpen) return

    this.isDrawerOpen = isOpen

    if (isOpen) {
      this.trapDrawerFocus()
    } else {
      this.releaseDrawerFocus()
    }
  }

  trapDrawerFocus () {
    const container = document.querySelector(this.drawerMenuSelector)
    if (!container) return

    trapFocus(container)

    this.makeCloseIconAccessible(container)
    this.focusFirstVisibleElement(container)

    // The vendor drawer does async work (e.g. a b2b_store check) shortly
    // after opening and re-renders its content, which detaches whatever
    // we focused and resets focus to <body>. Retry until it settles.
    ;[150, 400, 800].forEach(delay => {
      setTimeout(() => {
        if (!this.isDrawerOpen) return

        const liveContainer = document.querySelector(this.drawerMenuSelector)
        if (!liveContainer) return

        this.makeCloseIconAccessible(liveContainer)

        if (document.activeElement !== document.body) return
        this.focusFirstVisibleElement(liveContainer)
      }, delay)
    })
  }

  makeCloseIconAccessible (container) {
    const closeIcon = container.querySelector(this.closeIconSelector)
    if (!closeIcon) return

    // The vendor drawer re-renders this node (e.g. when the list updates),
    // which can silently drop attributes we've added. Reapply every time,
    // unconditionally, rather than trusting a "already patched" flag.
    closeIcon.setAttribute('tabindex', '0')
    closeIcon.setAttribute('role', 'button')
    closeIcon.setAttribute('aria-label', 'Close')

    if (this.closeIconsWithListener.has(closeIcon)) return
    this.closeIconsWithListener.add(closeIcon)

    closeIcon.addEventListener('keydown', e => {
      if (e.key !== 'Enter' && e.key !== ' ') return
      e.preventDefault()
      closeIcon.click()
    })
  }

  focusFirstVisibleElement (container) {
    const candidates = container.querySelectorAll(
      'button:not([disabled]), a[href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
    )

    const focusable = Array.from(candidates).find(el => {
      const rect = el.getBoundingClientRect()
      return rect.width > 0 && rect.height > 0
    })

    if (focusable) {
      focusable.focus()
    } else {
      container.setAttribute('tabindex', '-1')
      container.focus()
    }
  }

  releaseDrawerFocus () {
    disableTrapFocus()

    if (this.activeTrigger && document.contains(this.activeTrigger)) {
      this.activeTrigger.focus()
    } else {
      document.querySelector(this.buttonSelector)?.focus()
    }
  }

  handleKeydown (e) {
    if (e.key !== 'Escape' || !this.isDrawerOpen) return

    document.querySelector(this.backdropSelector)?.click()
  }

  updatePriceFormatting () {
    const priceSpanEls = document.querySelectorAll('.price-regular span')
    priceSpanEls.forEach(priceSpanEl => {
      priceSpanEl.innerText = priceSpanEl.innerText.replace(/[.,]00\b/g, '')
    })
  }

  handleClick (e) {
    const trigger = e.target.closest(this.buttonSelector)

    if (trigger) {
      this.activeTrigger = trigger
    }

    if (e.target.closest(this.drawerSelector) || trigger) {
      this.updateWishlistCount()
    }

    if (e.target.closest(this.drawerAddToCartSelector)) {
      this.addToCartClick(e.target)
    }
  }

  addToCartClick (target) {
    const atcDisabled = document.body.classList.contains(
      'lyt-Theme-wishlistAtcDisabled',
    )
    if (!atcDisabled) return

    const productLineItem = target.closest('.iwish-row.iwishProduct-row')
    if (productLineItem) {
      productLineItem.querySelector('p.product-title')?.click()
    }
  }

  onVariantChanged (e) {
    const variantId = e.detail?.variant?.data?.id

    if (variantId) {
      document
        .querySelector(this.singleProductSelector)
        ?.setAttribute('data-variant', variantId)
    }
  }

  updateWishlistCount () {
    try {
      if (typeof iWish !== 'undefined') {
        /* eslint-disable-next-line no-undef */
        const wishlistData = iWish.getWishlist()
        const count = JSON.parse(wishlistData)?.length

        const countEls = document.querySelectorAll(this.countSelector)

        countEls.forEach(countEl => {
          countEl.dataset.count = count
          countEl.innerHTML = count
        })
      }
    } catch (err) {
      console.error(err)
    }
  }

  get drawerAddToCartSelector () {
    return `#iwish-drawer-root div.cart-button`
  }

  get drawerSelector () {
    return `#iwish-drawer-root`
  }

  get drawerMenuSelector () {
    return `.iwish-drawer-menu`
  }

  get drawerVisibilityClass () {
    return `wishlist-drawer-show`
  }

  get backdropSelector () {
    return `.iwish-backdrop`
  }

  get closeIconSelector () {
    return `.mst-drawer-title-icon`
  }

  get buttonSelector () {
    return `[data-wishlist-app-external-el="button"]`
  }

  get singleProductSelector () {
    return `[data-wishlist-app-external-el="button"][data-type="single-product"]`
  }

  get countSelector () {
    return `[data-wishlist-app-external-el="count"]`
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.BAO.WishlistApp = new WishlistApp()
})
